import { Vector2, Vector3, PlaneGeometry, MeshBasicMaterial, Mesh, DoubleSide } from 'three';
import { CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import { VapiTranscription } from '../vapi-UI/VapiTranscription.js';
import { ReactiveComponent } from '../../core/ReactiveComponent.core.js';
import { computeProjectiveMatrix3d } from '../../plugins/3d-2d-transform/src/projectiveMatrix.js';
import { vapiTranscriptionPerspectiveCornerHandleStyle } from '../../constants/vapiPerspectiveHandles.js';

/** Presets for transcription container (portrait vs landscape). Overridden by dbConfig.transcriptionWidth/Height. */
const TRANSCRIPTION_PRESETS = {
    portrait: { width: 280, height: 400 },
    landscape: { width: 400, height: 220 }
};

/** Example text shown in edit mode for previewing colors and layout. Uses inline styles for dbConfig colors. */
const buildEditPreviewHtml = (cfg) => {
    const trans = (key, fallback) => {
        if (typeof $trans !== 'function') return fallback;
        const value = $trans(key, {}, fallback);
        return value || fallback;
    };
    const userColor = cfg?.transcriptionUserColor ?? '#7dd3fc';
    const userRoleColor = cfg?.transcriptionUserRoleColor ?? '#ffffff';
    const aiColor = cfg?.transcriptionAIColor ?? '#00d4aa';
    const aiRoleColor = cfg?.transcriptionAIRoleColor ?? '#00d4aa';
    const userLabel = trans('you-text', 'You');
    const userExample = trans('vrai-agent-transcription-preview-user-line', 'Hello, can you help me?');
    const agentLabel = trans('vrai-agent-text', 'Agent');
    const agentExample = trans('vrai-agent-transcription-preview-agent-line', 'Of course! What would you like to know?');
    return `<div class="subtitle-entry subtitle-user" style="color:${userColor}"><span class="subtitle-role" style="color:${userRoleColor}">${userLabel}:</span> ${userExample}</div><div class="subtitle-entry subtitle-agent" style="color:${aiColor}"><span class="subtitle-role" style="color:${aiRoleColor}">${agentLabel}:</span> ${agentExample}</div>`;
};

/**
 * VRVapiTranscriptionWithCSS3D - VAPI transcription window.
 * Uses CSS3DObject in scene with an invisible 3D anchor mesh.
 * Same pattern as VRVapiChairWithCSS3D and VRVapiCameraWithCSS3D.
 * Supports dbConfig: transcriptionWidth, transcriptionHeight, transcriptionPreset ('portrait'|'landscape').
 */
class VRVapiTranscriptionWithCSS3D extends ReactiveComponent {
    constructor(params = {}) {
        super(null, {
            mode: params.editMode ? 'edit' : 'view'
        });

        const { model, camera, controls, raycaster, position, id, dbConfig, sizes } = params;
        this.id = id;
        this.model = model;
        this.camera = camera;
        this.controls = controls || null;
        this.position = position || { x: 0, y: 0, z: -900 };
        this.dbConfig = dbConfig || {};
        this._raycaster = raycaster || null;
        this._sizes = sizes || { width: window.innerWidth, height: window.innerHeight };
        this._viewportEl = params.viewportElement || document.querySelector('#webgl') || document.querySelector('#css3d');
        this._ndc = new Vector2();
        this._tmpVec = new Vector3();
        this._dragFixedDistance = null;
        this._dragDirOffset = new Vector3();
        this._dragGrabOffset = new Vector3();
        this._dragOffsetX = 0;
        this._dragOffsetY = 0;
        this.isDragging = false;
        this._captureEl = null;
        this._isEditMode = !!params.editMode;
        this._css3dScale = 1;
        /** Uniform scale of the CSS3D plane (same pattern as chair / user camera); persisted as dbConfig.size. */
        this._userScale = Number(this.dbConfig?.size ?? 1) || 1;
        this._rootTopOffset = '0px';
        this.parent = this;
        this.name = 'vapiTranscription';
        this.type = 'vapiTranscription';
        this.vraiAgentRoot = null;
        this._subtitleHistory = [];
        this._liveSubtitleEntry = null;

        this._buildNode();
        this.anchor.position.set(
            this.position.x,
            this.position.y,
            this.position.z !== undefined ? this.position.z : -600
        );
        this.anchor.userData.vraiAgentTranscription = this;
        if (this.camera) {
            const camPos = new Vector3();
            this.camera.getWorldPosition(camPos);
            this.anchor.lookAt(camPos);
        }

        if (this.state.mode === 'edit') {
            this._attachDragListeners();
        }
        this._setupTranscriptListeners();
        this.applyTranscriptionStyles();
        this._applyVisibilityFromConfig();

        return this;
    }

    _getOwningChair() {
        const chairId = this.dbConfig?.vapiAgentId ?? null;
        const chairs = this.model?.view?.vraiAgentChairs || [];
        if (!chairId || !Array.isArray(chairs)) return null;
        return chairs.find(c => String(c.getId?.() ?? c.id) === String(chairId)) || null;
    }

    _getMenuDisplayName() {
        const t = typeof $trans !== 'undefined' && $trans('vrai-agent-transcription');
        const componentName = t?.text || t || 'Transcription';
        const chair = this._getOwningChair();
        const ownerName = (chair?.dbConfig?.chairName || chair?.dbConfig?.name || '').trim();
        const ownName = (this.dbConfig?.name || '').trim();
        if (ownerName && ownName) return `${ownerName} - ${componentName}: ${ownName}`;
        if (ownerName) return `${ownerName} - ${componentName}`;
        return ownName ? `${componentName}: ${ownName}` : componentName;
    }

    _applyVisibilityFromConfig() {
        const chair = this._getOwningChair();
        // If chair already exists, mirror its visibility directly.
        if (chair?.anchor) {
            if (this.anchor) this.anchor.visible = chair.anchor.visible !== false;
            this._syncDomVisibilityToAnchor();
            return;
        }

        const inEditMode = !!this.model?.editMode || this.state.mode === 'edit';
        const chairCfg = chair?.dbConfig || {};

        const chairActive = chairCfg?.components?.chair?.active !== false;
        const chairHideOnStart = !!(chairCfg?.hideOnStart || chairCfg?.components?.chair?.hideOnStart);
        const hiddenByHideOnStart = !inEditMode && chairHideOnStart;

        // Transcription visibility mirrors its owning vraiAgent (chair):
        // no separate show/hide flags for transcription itself.
        const visible = inEditMode ? true : (chairActive && !hiddenByHideOnStart);

        if (this.anchor) this.anchor.visible = visible;
        this._syncDomVisibilityToAnchor();
    }

    _syncDomVisibilityToAnchor() {
        if (!this.dom || !this.dom.style) return;
        const visible = this.anchor?.visible !== false;
        this.dom.classList.toggle('visible', visible);
        this.dom.classList.toggle('hide-on-start', !visible);
        this.dom.style.display = visible ? '' : 'none';
    }

    _perspectiveOffsetsForDb() {
        const w = this.dom?.offsetWidth || this._containerWidth || 1;
        const h = this.dom?.offsetHeight || this._containerHeight || 1;
        return (this._perspectiveCorners || []).map(p => ({ x: p.x, y: p.y, xr: p.x / w, yr: p.y / h }));
    }

    _cornersFromPerspectiveOffsets(saved, w, h) {
        if (!Array.isArray(saved) || saved.length !== 4) return null;
        return saved.map((p, i) => {
            const xFromRatio = Number(p?.xr) * w;
            const yFromRatio = Number(p?.yr) * h;
            if (p?.xr !== undefined && p?.yr !== undefined && Number.isFinite(xFromRatio) && Number.isFinite(yFromRatio)) {
                return { x: xFromRatio, y: yFromRatio };
            }
            const xFromAbs = Number(p?.x);
            const yFromAbs = Number(p?.y);
            const fallback = [{ x: 0, y: 0 }, { x: w, y: 0 }, { x: w, y: h }, { x: 0, y: h }][i];
            return {
                x: Number.isFinite(xFromAbs) ? xFromAbs : fallback.x,
                y: Number.isFinite(yFromAbs) ? yFromAbs : fallback.y
            };
        });
    }

    _hasFinitePerspectiveCorners(corners = this._perspectiveCorners) {
        return Array.isArray(corners)
            && corners.length === 4
            && corners.every((p) => Number.isFinite(Number(p?.x)) && Number.isFinite(Number(p?.y)));
    }

    _syncHandlePositions() {
        const handles = this.dom?.querySelectorAll('.vapi-transcription2-corner-handle');
        handles?.forEach((h, i) => {
            if (this._perspectiveCorners[i]) {
                h.style.left = this._perspectiveCorners[i].x + 'px';
                h.style.top = this._perspectiveCorners[i].y + 'px';
            }
        });
    }

    _resetPerspectiveToOriginalRect() {
        if (!this._originalPos || !this._perspectiveCorners) return;
        this._perspectiveCorners.splice(0, 4, ...this._originalPos.map(p => ({ ...p })));
        this._syncHandlePositions();
        this._applyPerspectiveTransform?.();
        this.dbConfig = { ...this.dbConfig, perspectiveOffsets: this._perspectiveOffsetsForDb() };
    }

    applyTranscriptionStyles() {
        const root = this.dom;
        if (!root) return;
        const scene = this.model?.view?.currentScene ?? this.model?.currentScene;
        const sceneObj = scene?.objects?.find(o => o.type === 'vapiTranscription' && (String(o.id) === String(this.id) || String(o.unique_id) === String(this.id)));
        const cfg = sceneObj || this.dbConfig || {};
        this._lastTranscriptionCfg = cfg;
        const userColor = cfg.transcriptionUserColor ?? '#7dd3fc';
        const userRoleColor = cfg.transcriptionUserRoleColor ?? '#ffffff';
        const aiColor = cfg.transcriptionAIColor ?? '#00d4aa';
        const aiRoleColor = cfg.transcriptionAIRoleColor ?? '#00d4aa';
        const bgColor = cfg.transcriptionBackgroundColor ?? 'transparent';
        const vars = {
            '--transcription-user-color': userColor,
            '--transcription-user-role-color': userRoleColor,
            '--transcription-ai-color': aiColor,
            '--transcription-ai-role-color': aiRoleColor,
            '--transcription-bg-color': bgColor
        };
        Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
        const windowEl = root.querySelector('.vrai-transcription-window');
        if (windowEl) {
            Object.entries(vars).forEach(([k, v]) => windowEl.style.setProperty(k, v));
            windowEl.style.background = bgColor;
        }
        if (this.state.mode === 'edit') this._renderEditPreview();
    }

    _renderEditPreview() {
        const el = this.dom?.querySelector('.vrai-transcription-window');
        if (!el || this.state.mode !== 'edit') return;
        if (this._subtitleHistory.length > 0) return;
        el.innerHTML = buildEditPreviewHtml(this._lastTranscriptionCfg || this.dbConfig);
        el.classList.remove('empty');
    }

    _buildNode() {
        const preset = this.dbConfig?.transcriptionPreset || 'landscape';
        const presetDims = TRANSCRIPTION_PRESETS[preset] || TRANSCRIPTION_PRESETS.landscape;
        const w = this.dbConfig?.transcriptionWidth ?? presetDims.width;
        const h = this.dbConfig?.transcriptionHeight ?? presetDims.height;
        this._containerWidth = w;
        this._containerHeight = h;

        const transcriptionInstance = new VapiTranscription({ width: w, height: h });
        const transcriptionContent = transcriptionInstance.render();
        transcriptionContent.style.width = '100%';
        transcriptionContent.style.height = '100%';
        if (this._isEditMode) {
            transcriptionContent.style.pointerEvents = 'none';
        }
        this._vraiComponentContent = transcriptionInstance.getContentElement();
        const transcriptionWindowEl = transcriptionInstance.getWindowElement();
        if (this._isEditMode && transcriptionWindowEl) {
            transcriptionWindowEl.style.pointerEvents = 'none';
        }

        const root = document.createElement('div');
        root.setAttribute('class', 'vrai-agent-component vapi_transcription2_wrapper vrai-agent-2d-indicator vapi-transcription2d-indicator');
        root.setAttribute('data-css3d-interactive', 'true');
        // Only treat transcription as CSS3D-clickable when interacting with edit affordances.
        // In view mode this lets clicks pass through to WebGL objects behind it.
        root.setAttribute('data-css3d-click-target', '.vapi-transcription2-drag-surface, .vapi-transcription2-corner-handle');
        root.setAttribute('data-id', `vapi_transcription2_${this.id}`);
        root.style.cssText = `
            position: relative;
            width: ${w}px;
            height: ${h}px;
            pointer-events: auto;
            will-change: transform;
            user-select: none;
            -webkit-user-select: none;
            background: transparent;
            backface-visibility: hidden;
            -webkit-backface-visibility: hidden;
        `;
        // Keep visual appearance consistent between edit/view; editing affordances are added via
        // handles/outline classes, not by dimming the component.
        root.style.opacity = '1';
        root.classList.add(`vapi-transcription2d-${this.id}`);

        this._originalPos = [{ x: 0, y: 0 }, { x: w, y: 0 }, { x: w, y: h }, { x: 0, y: h }];
        const saved = this.dbConfig?.perspectiveOffsets;
        this._perspectiveCorners = this._cornersFromPerspectiveOffsets(saved, w, h)
            ?? this._originalPos.map(p => ({ ...p }));

        const perspectiveContent = document.createElement('div');
        perspectiveContent.className = 'vapi-transcription2-perspective-content';
        perspectiveContent.style.cssText = 'position:absolute;inset:0;overflow:visible;transform-origin:0 0;pointer-events:none;background:transparent;backface-visibility:hidden;-webkit-backface-visibility:hidden;transform-style:preserve-3d;will-change:transform;';
        perspectiveContent.appendChild(transcriptionContent);
        this._perspectiveContent = perspectiveContent;

        const perspectiveWrapper = document.createElement('div');
        perspectiveWrapper.className = 'vapi-transcription2-perspective-wrapper';
        perspectiveWrapper.style.cssText = 'position:absolute;inset:0;overflow:visible;pointer-events:none;background:transparent;backface-visibility:hidden;-webkit-backface-visibility:hidden;transform-style:preserve-3d;';
        perspectiveWrapper.appendChild(perspectiveContent);

        this._applyPerspectiveTransform = () => {
            const ew = root.offsetWidth || this._containerWidth || w;
            const eh = root.offsetHeight || this._containerHeight || h;
            if (!this._hasFinitePerspectiveCorners()) {
                this._perspectiveCorners = [{ x: 0, y: 0 }, { x: ew, y: 0 }, { x: ew, y: eh }, { x: 0, y: eh }];
                this._syncHandlePositions?.();
            }
            const currentOrigPos = [{ x: 0, y: 0 }, { x: ew, y: 0 }, { x: ew, y: eh }, { x: 0, y: eh }];
            const m = computeProjectiveMatrix3d(currentOrigPos, this._perspectiveCorners);
            perspectiveContent.style.transform = m;
            // Same `top` in edit and view — a view-only offset here made the panel ~25–30px higher in view than in edit.
            root.style.setProperty('top', this._rootTopOffset, 'important');
        };
        this._applyPerspectiveTransform();

        for (let i = 0; i < 4; i++) {
            const hnd = document.createElement('div');
            hnd.className = 'vapi-transcription2-corner-handle';
            hnd.dataset.corner = String(i);
            hnd.style.cssText = vapiTranscriptionPerspectiveCornerHandleStyle('pointer-events:none;display:none;');
            const updateHandlePos = () => { hnd.style.left = this._perspectiveCorners[i].x + 'px'; hnd.style.top = this._perspectiveCorners[i].y + 'px'; };
            updateHandlePos();
            if (this.state.mode === 'edit') {
                hnd.style.display = '';
                hnd.style.pointerEvents = 'auto';
                const onCornerPointerDown = (e) => {
                    if (this.dbConfig?.lockedInEdit) return;
                    e.preventDefault();
                    e.stopPropagation();
                    hnd.style.cursor = 'grabbing';
                    const startX = e.clientX, startY = e.clientY;
                    const startPos = { x: this._perspectiveCorners[i].x, y: this._perspectiveCorners[i].y };
                    const onMove = (ev) => {
                        const dx = ev.clientX - startX, dy = ev.clientY - startY;
                        this._perspectiveCorners[i].x = startPos.x + dx;
                        this._perspectiveCorners[i].y = startPos.y + dy;
                        updateHandlePos();
                        this._applyPerspectiveTransform();
                    };
                    const onUp = () => {
                        hnd.style.cursor = 'grab';
                        document.removeEventListener('pointermove', onMove);
                        document.removeEventListener('pointerup', onUp);
                        this.dbConfig = { ...this.dbConfig, perspectiveOffsets: this._perspectiveOffsetsForDb() };
                        if (this.model?.view?.updateJSON) this.model.view.updateJSON(this.anchor, null);
                    };
                    document.addEventListener('pointermove', onMove);
                    document.addEventListener('pointerup', onUp);
                };
                hnd.addEventListener('pointerdown', onCornerPointerDown);
            }
            perspectiveWrapper.appendChild(hnd);
        }

        // Edit: drag must use an untransformed w×h layer. Homography on perspectiveContent can map
        // the panel to a huge screen-space hit region and steal orbit / canvas input.
        if (this.state.mode === 'edit') {
            this._dragSurfaceEl = document.createElement('div');
            this._dragSurfaceEl.className = 'vapi-transcription2-drag-surface';
            this._dragSurfaceEl.style.cssText = 'position:absolute;left:0;top:0;right:0;bottom:0;width:100%;height:100%;cursor:grab;pointer-events:auto;';
            this._onDragSurfacePointerDown = (e) => {
                if (e.button !== 0) return;
                if (this.state.mode !== 'edit' || !this._onPointerDown) return;
                e.preventDefault();
                e.stopPropagation();
                this._onPointerDown(e);
            };
            this._dragSurfaceEl.addEventListener('pointerdown', this._onDragSurfacePointerDown);
            root.appendChild(this._dragSurfaceEl);
        }

        root.appendChild(perspectiveWrapper);

        // Re-apply perspective whenever the element enters the CSS3D DOM or transitions
        // from display:none to visible. This fires on the first CSS3DRenderer append
        // (which happens when the anchor is added to the scene via a show action) and
        // on every subsequent hide→show cycle. The chair component uses the same pattern.
        // NOTE: render() is NOT called for transcriptions in the scene loop, so this
        // ResizeObserver is the only reliable trigger for post-construction re-application.
        if (typeof ResizeObserver !== 'undefined') {
            this._resizeObserver = new ResizeObserver((entries) => {
                const entry = entries[0];
                if (!entry) return;
                if (entry.contentRect.width <= 0 || entry.contentRect.height <= 0) return;
                this._applyPerspectiveTransform?.();
            });
            this._resizeObserver.observe(root);
        }

        this.root = root;
        this.dom = root;
        this.css3dObject = new CSS3DObject(root);
        this.css3dObject.scale.set(this._css3dScale, this._css3dScale, 1);

        const { width: anchorW, height: anchorH } = this._getAnchorSize(w, h);
        const geo = new PlaneGeometry(anchorW, anchorH);
        const mat = new MeshBasicMaterial({
            color: 0x00aaff,
            side: DoubleSide,
            transparent: true,
            opacity: 0,
            depthWrite: false
        });
        this.anchor = new Mesh(geo, mat);
        this.anchor.name = 'vapiTranscription';
        this.anchor.type = 'vapiTranscription';
        this.anchor.groupDetails = {
            title: (typeof $trans !== 'undefined' && $trans('vrai-agent-transcription')) || 'Transcription',
            icon: 'subtitles',
            editTitle: (typeof $trans !== 'undefined' && $trans('edit-vrai-agent-transcription')) || 'Edit Transcription',
            dataLang: 'edit-vrai-agent-transcription'
        };
        this.anchor.add(this.css3dObject);

        this.elementClass = `vapi-transcription2d-${this.id}`;
        this.mesh = this.anchor;
        this.object = this.anchor;
    }

    _getAnchorSize(widthPx, heightPx) {
        // Keep a stable world-space footprint with transcription aspect ratio.
        const baseW = 64;
        const safeWidth = Math.max(1, Number(widthPx) || 1);
        const safeHeight = Math.max(1, Number(heightPx) || 1);
        return { width: baseW, height: baseW * (safeHeight / safeWidth) };
    }

    _updateAnchorGeometry(widthPx, heightPx) {
        if (!this.anchor) return;
        const { width, height } = this._getAnchorSize(widthPx, heightPx);
        if (this.anchor.geometry) this.anchor.geometry.dispose();
        this.anchor.geometry = new PlaneGeometry(width, height);
    }

    _setupTranscriptListeners() {
        this._vapiAgentId = this.dbConfig?.vapiAgentId || null;
        this._subtitlesEl = this.dom?.querySelector('.vrai-transcription-window') || null;

        if (this.model && this._vapiAgentId) {
            this._handleTranscript = (e) => {
                const d = (e && e.data) || {};
                if (String(d.vapiAgentId) !== String(this._vapiAgentId)) return;
                const role = d.role || 'agent';
                const text = d.transcript || '';
                if (!text) return;
                const isFinal = !!d.isFinal;
                if (!isFinal) {
                    this._setLiveSubtitle(role, text);
                    return;
                }
                this._clearLiveSubtitle(role);
                this._addSubtitle(role, text, { sentAt: d.sentAt, forceNewEntry: d.source === 'command' });
            };
            this._handleCallStart = (e) => {
                const d = (e && e.data) || {};
                if (String(d.vapiAgentId) !== String(this._vapiAgentId)) return;
                this._resetSubtitles();
            };
            this._handleAgentChanged = (e) => {
                const d = (e && e.data) || {};
                if (String(d.vapiAgentId) !== String(this._vapiAgentId)) return;
                this._resetSubtitles();
            };
            this.model.addEventListener('vapi-agent-transcript', this._handleTranscript);
            this.model.addEventListener('vapi-agent-call-start', this._handleCallStart);
            this.model.addEventListener('vapi-agent-changed', this._handleAgentChanged);
        }
    }

    _normalizeTranscriptText(text) {
        return String(text || '')
            .toLowerCase()
            .replace(/[\W_]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    }

    _mergeSubtitleText(prev, next) {
        if (!prev) return next;
        const prevTrimmed = prev.replace(/[\s.,;:!?]+$/, '');
        if (next.startsWith(prev) || (prevTrimmed && next.startsWith(prevTrimmed))) return next;
        const prefixLen = Math.min(prev.length, next.length);
        let commonPrefix = 0;
        for (let i = 0; i < prefixLen && prev[i] === next[i]; i++) commonPrefix++;
        if (commonPrefix >= 6 && next.length >= commonPrefix) return next;
        let overlap = Math.min(prev.length, next.length);
        while (overlap > 0) {
            if (prev.endsWith(next.slice(0, overlap))) return prev + next.slice(overlap);
            overlap--;
        }
        return prev + (/[\s.:,;!?]$/.test(prev) ? '' : ' ') + next;
    }

    _reconcileSubtitleText(prev, next) {
        if (!prev) return next;
        const normalizedPrev = this._normalizeTranscriptText(prev);
        const normalizedNext = this._normalizeTranscriptText(next);
        if (!normalizedNext) return prev;
        if (!normalizedPrev) return next;
        // Most VAPI transcript updates are growing/correcting the current utterance.
        // In that case we should replace the old chunk, not append to it.
        if (
            normalizedNext === normalizedPrev ||
            normalizedNext.startsWith(normalizedPrev) ||
            normalizedPrev.startsWith(normalizedNext)
        ) {
            return next;
        }
        return this._mergeSubtitleText(prev, next);
    }

    _setLiveSubtitle(role, text) {
        const transcript = (text || '').trim();
        if (!transcript) return;
        const normalizedRole = role === 'user' ? 'user' : 'assistant';
        this._liveSubtitleEntry = { role: normalizedRole, text: transcript };
        this._renderSubtitleHistory();
    }

    _clearLiveSubtitle(role = null) {
        if (!this._liveSubtitleEntry) return;
        if (role != null) {
            const normalizedRole = role === 'user' ? 'user' : 'assistant';
            if (this._liveSubtitleEntry.role !== normalizedRole) return;
        }
        this._liveSubtitleEntry = null;
        this._renderSubtitleHistory();
    }

    _addSubtitle(role, text, opts = {}) {
        const transcript = (text || '').trim();
        if (!transcript) return;
        const normalizedRole = role === 'user' ? 'user' : 'assistant';
        const sentAt = Number.isFinite(Number(opts.sentAt)) ? Number(opts.sentAt) : Date.now();
        const forceNewEntry = !!opts.forceNewEntry;
        const hadLiveForRole = !!(this._liveSubtitleEntry && this._liveSubtitleEntry.role === normalizedRole);
        if (hadLiveForRole) this._liveSubtitleEntry = null;
        const history = this._subtitleHistory;
        const last = history[history.length - 1];
        if (!forceNewEntry && last && last.role === normalizedRole) {
            const reconciled = this._reconcileSubtitleText(last.text, transcript);
            if (reconciled === last.text) {
                if (hadLiveForRole) this._renderSubtitleHistory();
                return;
            }
            last.text = reconciled;
        } else {
            history.push({ role: normalizedRole, text: transcript, sentAt });
        }
        this._renderSubtitleHistory();
    }

    _getRoleLabel(role) {
        const isUser = role === 'user';
        if (typeof $trans === 'undefined') return isUser ? 'You' : 'Agent';
        if (isUser) return $trans('you-text') || 'You';
        return $trans('vrai-agent-text') || 'Agent';
    }

    _resetSubtitles() {
        this._subtitleHistory = [];
        this._liveSubtitleEntry = null;
        const el = this.dom?.querySelector('.vrai-transcription-window');
        if (el) {
            if (this.state.mode === 'edit') {
                this._renderEditPreview();
            } else {
                el.innerHTML = '';
                el.classList.add('empty');
            }
        }
    }

    _renderSubtitleHistory() {
        const el = this.dom?.querySelector('.vrai-transcription-window');
        if (!el) return;
        const history = this._subtitleHistory;
        const liveSubtitleEntry = this._liveSubtitleEntry;
        if (history.length === 0 && !liveSubtitleEntry) {
            if (this.state.mode === 'edit') {
                this._renderEditPreview();
            } else {
                el.innerHTML = '';
                el.classList.add('empty');
            }
            return;
        }
        el.classList.remove('empty');
        const escapeHtml = (t) => {
            const div = document.createElement('div');
            div.textContent = t;
            return div.innerHTML;
        };
        this.applyTranscriptionStyles();
        const cfg = this._lastTranscriptionCfg || this.dbConfig || {};
        const userColor = (cfg.transcriptionUserColor || '#7dd3fc').trim() || '#7dd3fc';
        const userRoleColor = (cfg.transcriptionUserRoleColor || '#ffffff').trim() || '#ffffff';
        const aiColor = (cfg.transcriptionAIColor || '#00d4aa').trim() || '#00d4aa';
        const aiRoleColor = (cfg.transcriptionAIRoleColor || '#00d4aa').trim() || '#00d4aa';
        const committedHtml = history.map(({ role, text }) => {
            const label = this._getRoleLabel(role);
            const entryColor = role === 'user' ? userColor : aiColor;
            const roleColor = role === 'user' ? userRoleColor : aiRoleColor;
            const formattedText = escapeHtml(text).replace(/\n/g, '<br/>');
            return `<div class="subtitle-entry subtitle-${role}" style="color:${entryColor}"><span class="subtitle-role" style="color:${roleColor}">${label}:</span> ${formattedText}</div>`;
        }).join('\n\n');
        let liveHtml = '';
        if (liveSubtitleEntry) {
            const role = liveSubtitleEntry.role === 'user' ? 'user' : 'assistant';
            const label = this._getRoleLabel(role);
            const entryColor = role === 'user' ? userColor : aiColor;
            const roleColor = role === 'user' ? userRoleColor : aiRoleColor;
            const formattedText = escapeHtml(liveSubtitleEntry.text).replace(/\n/g, '<br/>');
            liveHtml = `<div class="subtitle-entry subtitle-${role} subtitle-live" style="color:${entryColor};opacity:0.7;font-style:italic;"><span class="subtitle-role" style="color:${roleColor}">${label}:</span> ${formattedText}</div>`;
        }
        el.innerHTML = committedHtml + liveHtml;
        el.scrollTop = el.scrollHeight;
    }

    _clientToNDC(clientX, clientY) {
        const el = this._viewportEl;
        if (!el) {
            this._ndc.x = (clientX / this._sizes.width) * 2 - 1;
            this._ndc.y = -(clientY / this._sizes.height) * 2 + 1;
            return;
        }
        const rect = el.getBoundingClientRect();
        this._ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        this._ndc.y = 1 - ((clientY - rect.top) / rect.height) * 2;
    }

    _setFromClientXY(clientX, clientY) {
        this._clientToNDC(clientX, clientY);
        this._raycaster.setFromCamera(this._ndc, this.camera);
        const camWorld = this.camera.getWorldPosition(new Vector3());
        // Same as VRVapiChairWithCSS3D: lock distance + direction offset so the panel tracks the cursor
        // on the sphere without plane/slide error (pixel-stable in edit vs view).
        const dir = this._raycaster.ray.direction.clone().normalize().add(this._dragDirOffset).normalize();
        this._tmpVec.copy(dir)
            .multiplyScalar(this._dragFixedDistance)
            .add(camWorld);
        if (this.anchor.parent) {
            this.anchor.parent.updateMatrixWorld(true);
            this.anchor.parent.worldToLocal(this._tmpVec);
            this.anchor.position.copy(this._tmpVec);
        } else {
            this.anchor.position.copy(this._tmpVec);
        }
    }

    _onPointerMove = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.isDragging) return;
        this._setFromClientXY(e.clientX, e.clientY);
        this.anchor.lookAt(this.camera.position);
    };

    _onPointerUp = (e) => {
        if (!this.isDragging) return;
        this.isDragging = false;
        if (this.camera) {
            this.anchor.lookAt(this.camera.position);
        }
        if (this._dragSurfaceEl) this._dragSurfaceEl.style.cursor = 'grab';
        if (this._captureEl) {
            try { this._captureEl.releasePointerCapture(e.pointerId); } catch (err) { }
            this._captureEl = null;
        }
        window.removeEventListener('pointermove', this._onPointerMove);
        window.removeEventListener('pointerup', this._onPointerUp);
        window.removeEventListener('pointercancel', this._onPointerUp);
        if (this.controls) this.controls.enabled = true;
        if (this.model?.view) {
            if (this.model.view.updateJSON) this.model.view.updateJSON(this.anchor, null);
            this.model.view.elementSelected = this;
            this.showObjectMenu();
            if (typeof $ !== 'undefined') $('.edit_element').trigger('click');
        }
    };

    _onPointerDown = (e) => {
        if (this.state.mode !== 'edit') return;
        if (this.dbConfig?.lockedInEdit) return;
        if (this.isDragging) return;
        if (e.target && (e.target.closest('input') || e.target.closest('textarea') || e.target.closest('button') || e.target.closest('a'))) return;
        if (e.target?.closest?.('.vapi-transcription2-corner-handle')) return;
        this._dragOffsetX = 0;
        this._dragOffsetY = 0;
        this.isDragging = true;
        this._captureEl = e.currentTarget || this.dom;
        try { this._captureEl?.setPointerCapture?.(e.pointerId); } catch (err) { }
        if (this.anchor.parent) {
            this.anchor.parent.updateMatrixWorld(true);
            this.anchor.getWorldPosition(this._tmpVec);
        } else {
            this._tmpVec.copy(this.anchor.position);
        }
        const camWorld = this.camera.getWorldPosition(new Vector3());
        this._dragFixedDistance = camWorld.distanceTo(this._tmpVec);
        const objDir = this._tmpVec.clone().sub(camWorld).normalize();
        this._clientToNDC(e.clientX, e.clientY);
        this._raycaster.setFromCamera(this._ndc, this.camera);
        const cursorDir = this._raycaster.ray.direction.clone().normalize();
        this._dragDirOffset.copy(objDir).sub(cursorDir);
        this._dragGrabOffset.set(0, 0, 0);
        if (this._dragSurfaceEl) this._dragSurfaceEl.style.cursor = 'grabbing';
        window.addEventListener('pointermove', this._onPointerMove);
        window.addEventListener('pointerup', this._onPointerUp);
        window.addEventListener('pointercancel', this._onPointerUp);
        if (this.controls) this.controls.enabled = false;
    };

    _attachDragListeners() {
        if (this._dragSurfaceEl) this._dragSurfaceEl.style.cursor = 'grab';
    }

    _detachDragListeners() {
        if (this._dragSurfaceEl) this._dragSurfaceEl.style.cursor = '';
        window.removeEventListener('pointermove', this._onPointerMove);
        window.removeEventListener('pointerup', this._onPointerUp);
        window.removeEventListener('pointercancel', this._onPointerUp);
        this.isDragging = false;
        if (this.controls) this.controls.enabled = true;
        if (this._applyPerspectiveTransform) this._applyPerspectiveTransform();
    }

    _getViewportScale() {
        const vpEl = this._viewportEl;
        const canvasH = vpEl?.clientHeight || 0;
        const winH = window.innerHeight;
        return (canvasH > 0 && winH > canvasH) ? winH / canvasH : 1;
    }

    render(camera) {
        if (!camera) return;
        if (!this.isDragging) {
            this.anchor.lookAt(camera.position);
            this._css3dScale = this._getViewportScale();
            const scale = this._css3dScale * (this._userScale ?? 1);
            if (this.css3dObject) this.css3dObject.scale.set(scale, scale, 1);
        }
        const chair = this._getOwningChair();
        if (chair?.anchor) this.anchor.visible = chair.anchor.visible !== false;
        this._syncDomVisibilityToAnchor();
    }

    getId() {
        return this.vraiAgentRoot ? this.vraiAgentRoot.getId() : this.id;
    }

    getDbConfig() {
        return this.vraiAgentRoot ? this.vraiAgentRoot.getDbConfig() : this.dbConfig;
    }

    setDbConfig(config) {
        const prevPreset = this.dbConfig?.transcriptionPreset || 'landscape';
        this.dbConfig = { ...this.dbConfig, ...config };
        this._vapiAgentId = this.dbConfig?.vapiAgentId ?? this._vapiAgentId;
        if (config?.size !== undefined) {
            const s = Number(config.size);
            this._userScale = Number.isFinite(s) && s > 0 ? s : 1;
        }
        if (config?.perspectiveOffsets && Array.isArray(config.perspectiveOffsets) && this._perspectiveCorners) {
            const w = this.dom?.offsetWidth || this._containerWidth || 1;
            const h = this.dom?.offsetHeight || this._containerHeight || 1;
            const normalized = this._cornersFromPerspectiveOffsets(config.perspectiveOffsets, w, h);
            if (normalized) {
                this._perspectiveCorners.splice(0, 4, ...normalized);
                this._syncHandlePositions();
                this._applyPerspectiveTransform?.();
            }
        }
        if (config?.transcriptionUserColor != null || config?.transcriptionUserRoleColor != null || config?.transcriptionAIColor != null || config?.transcriptionAIRoleColor != null || config?.transcriptionBackgroundColor != null) {
            this.applyTranscriptionStyles();
        }
        const preset = this.dbConfig?.transcriptionPreset || 'landscape';
        const presetDims = TRANSCRIPTION_PRESETS[preset] || TRANSCRIPTION_PRESETS.landscape;
        const presetChanged = config.transcriptionPreset != null && config.transcriptionPreset !== prevPreset;
        const newW = presetChanged ? presetDims.width : (config.transcriptionWidth ?? this.dbConfig?.transcriptionWidth ?? presetDims.width);
        const newH = presetChanged ? presetDims.height : (config.transcriptionHeight ?? this.dbConfig?.transcriptionHeight ?? presetDims.height);
        const sizeChanged = newW !== this._containerWidth || newH !== this._containerHeight;
        if (this.dom && (presetChanged || sizeChanged)) {
            this._containerWidth = newW;
            this._containerHeight = newH;
            this.dom.style.width = `${newW}px`;
            this.dom.style.height = `${newH}px`;
            this._updateAnchorGeometry(newW, newH);
            this._originalPos = [{ x: 0, y: 0 }, { x: newW, y: 0 }, { x: newW, y: newH }, { x: 0, y: newH }];
            this._resetPerspectiveToOriginalRect();
        }
        this._applyVisibilityFromConfig();
        return this.vraiAgentRoot ? this.vraiAgentRoot.setDbConfig(config) : null;
    }

    getLimits() {
        return this.vraiAgentRoot ? this.vraiAgentRoot.getLimits() : { min: 280, max: 800, distance: 540 };
    }

    getAxisRotations() {
        return this.vraiAgentRoot ? this.vraiAgentRoot.getAxisRotations() : { rotateX: 0, rotateY: 0 };
    }

    setDistance(val) {
        return this.vraiAgentRoot ? this.vraiAgentRoot.setDistance(val) : null;
    }

    setAxisRotations(axis, angle) {
        if (this.vraiAgentRoot) this.vraiAgentRoot.setAxisRotations(axis, angle);
    }

    showObjectMenu() {
        $('.object-menu .object-name').text(this._getMenuDisplayName());
        $('.object-menu').addClass('is-active');
        $('.object-menu').addClass('edit');
        $('.vrai-agent-settings').addClass('is-active');
        $('.perspective-settings:not(.vrai-agent-perspective), .tooltip-settings, .video-loop, .iframe-agent-settings').addClass('hidden');
    }

    toggleSelect() {
        this.dom?.querySelector('.vrai-transcription-window')?.classList?.toggle('active');
    }

    resetPerspective() {
        this._resetPerspectiveToOriginalRect();
    }

    disposeCss3D() {
        if (this.model && this._handleTranscript) {
            this.model.removeEventListener('vapi-agent-transcript', this._handleTranscript);
            this._handleTranscript = null;
        }
        if (this.model && this._handleCallStart) {
            this.model.removeEventListener('vapi-agent-call-start', this._handleCallStart);
            this._handleCallStart = null;
        }
        if (this.model && this._handleAgentChanged) {
            this.model.removeEventListener('vapi-agent-changed', this._handleAgentChanged);
            this._handleAgentChanged = null;
        }
    }

    destroy() {
        this.disposeCss3D();
        try { this._detachDragListeners(); } catch (e) { }
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
        if (this._dragSurfaceEl && this._onDragSurfacePointerDown) {
            try {
                this._dragSurfaceEl.removeEventListener('pointerdown', this._onDragSurfacePointerDown);
            } catch (e) { }
        }
        if (this.dom?.parentNode) this.dom.parentNode.removeChild(this.dom);
    }
}

export { VRVapiTranscriptionWithCSS3D };
